import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FileDown, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../common/ui';
import { saveBlob, blobErrorMessage } from '../../utils/downloadBlob';
import type { GkExportBatch } from './types';

/**
 * History of the GK exports (GK_EXPORT_PLAN.md 4.15, milestone M9): stored
 * XML and control file, "AJMP confirmed import", and Cancel with a reason.
 */

const inputClass =
  'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

const STATUS_LABEL: Record<GkExportBatch['status'], string> = {
  running: 'running',
  success: 'exported',
  failed: 'failed',
  cancelled: 'cancelled',
};

const REASON_LABEL: Record<string, string> = {
  deleted_in_gk: 'AJMP deleted the journal in GK',
  never_imported: 'the file was never imported',
};

const CancelForm: React.FC<{ batch: GkExportBatch; onDone: () => void; onClose: () => void }> = ({ batch, onDone, onClose }) => {
  const [reason, setReason] = useState<'deleted_in_gk' | 'never_imported'>('deleted_in_gk');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await axios.post(`/api/accounting/gk-export/exports/${batch.id}/cancel`, { reason, note: note.trim() || null });
      onDone();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not cancel the export.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm">
      <p className="mb-2 text-gray-700">
        Cancel only when the journal is not in GK. The payments of the cancelled documents can then be exported again, with new document numbers.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <select aria-label="Cancel reason" value={reason} onChange={(e) => setReason(e.target.value as 'deleted_in_gk' | 'never_imported')} className={inputClass}>
          <option value="deleted_in_gk">{REASON_LABEL.deleted_in_gk}</option>
          <option value="never_imported">{REASON_LABEL.never_imported}</option>
        </select>
        <input aria-label="Cancel note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className={`${inputClass} w-64`} />
        <Button variant="danger" onClick={submit} disabled={busy}>
          {busy ? 'Cancelling…' : 'Cancel export'}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Close
        </Button>
      </div>
      {error && <p className="mt-2 text-red-700">{error}</p>}
    </div>
  );
};

const GkHistory: React.FC<{ refreshKey: number; enabled: boolean }> = ({ refreshKey, enabled }) => {
  const [batches, setBatches] = useState<GkExportBatch[]>([]);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get<{ exports: GkExportBatch[] }>('/api/accounting/gk-export/exports');
      setBatches(res.data.exports);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not load the export history.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const download = async (batch: GkExportBatch, which: 'file' | 'summary') => {
    setError('');
    try {
      const res = await axios.get(`/api/accounting/gk-export/exports/${batch.id}/${which}`, { responseType: 'blob' });
      saveBlob(res.data, (which === 'file' ? batch.file_name : batch.summary_name) || `gk-export-${batch.id}`);
    } catch (err: any) {
      setError(await blobErrorMessage(err, 'Could not download the file.'));
    }
  };

  const confirm = async (batch: GkExportBatch) => {
    setError('');
    try {
      await axios.post(`/api/accounting/gk-export/exports/${batch.id}/confirm-import`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save the confirmation.');
    }
  };

  if (batches.length === 0 && !error) return <p className="text-sm text-gray-500">No exports yet.</p>;

  return (
    <div>
      {error && <p className="mb-2 text-sm text-red-700">{error}</p>}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="py-1 pr-4">Exported</th>
              <th className="py-1 pr-4">Currency</th>
              <th className="py-1 pr-4">Range</th>
              <th className="py-1 pr-4">Documents</th>
              <th className="py-1 pr-4">Status</th>
              <th className="py-1 pr-4">Files</th>
              <th className="py-1 pr-4">AJMP import</th>
              <th className="py-1 pr-4" />
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <React.Fragment key={b.id}>
                <tr className="border-t border-gray-100 align-top">
                  <td className="whitespace-nowrap py-1 pr-4">
                    {new Date(b.started_at).toLocaleString('sl-SI')}
                    {b.created_by_name && <div className="text-xs text-gray-500">{b.created_by_name}</div>}
                  </td>
                  <td className="py-1 pr-4">{b.currency}</td>
                  <td className="whitespace-nowrap py-1 pr-4">{b.date_from} – {b.date_to}</td>
                  <td className="py-1 pr-4">
                    {b.documents.map((d) => (
                      <div key={d.id} className={d.status === 'active' ? '' : 'text-gray-400 line-through'}>
                        <span className="whitespace-nowrap">{d.dokument}</span> <span className="whitespace-nowrap text-xs text-gray-500">({d.statementDay})</span>
                      </div>
                    ))}
                  </td>
                  <td className="py-1 pr-4">
                    <span className={b.status === 'success' ? 'text-green-700' : b.status === 'failed' ? 'text-red-700' : 'text-gray-600'}>
                      {STATUS_LABEL[b.status]}
                    </span>
                    {b.statement_control_overridden && <div className="text-xs text-amber-700">without statement control</div>}
                    {b.status === 'failed' && b.error && <div className="text-xs text-red-700">{b.error}</div>}
                    {b.status === 'cancelled' && b.cancel_reason && (
                      <div className="text-xs text-gray-500">{REASON_LABEL[b.cancel_reason] || b.cancel_reason}{b.cancel_note ? `: ${b.cancel_note}` : ''}</div>
                    )}
                  </td>
                  <td className="py-1 pr-4">
                    {b.file_name && (
                      <div className="flex flex-col gap-1">
                        <button type="button" className="flex items-center gap-1 text-blue-600 hover:underline" onClick={() => download(b, 'file')}>
                          <FileDown className="h-4 w-4" /> XML
                        </button>
                        <button type="button" className="flex items-center gap-1 text-blue-600 hover:underline" onClick={() => download(b, 'summary')}>
                          <FileDown className="h-4 w-4" /> Control file
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="py-1 pr-4">
                    {b.imported_confirmed_at ? (
                      <span className="flex items-center gap-1 text-green-700">
                        <CheckCircle2 className="h-4 w-4" /> {new Date(b.imported_confirmed_at).toLocaleDateString('sl-SI')}
                      </span>
                    ) : b.status === 'success' ? (
                      <Button variant="secondary" onClick={() => confirm(b)} disabled={!enabled}>
                        AJMP confirmed import
                      </Button>
                    ) : null}
                  </td>
                  <td className="py-1 pr-4">
                    {/* Only an export batch can be cancelled; the server refuses a correction batch. */}
                    {b.status === 'success' && b.kind === 'export' && (
                      <Button variant="secondary" icon={XCircle} onClick={() => setCancelling(cancelling === b.id ? null : b.id)} disabled={!enabled}>
                        Cancel
                      </Button>
                    )}
                  </td>
                </tr>
                {cancelling === b.id && (
                  <tr>
                    <td colSpan={8}>
                      <CancelForm batch={b} onClose={() => setCancelling(null)} onDone={() => { setCancelling(null); load(); }} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GkHistory;
